<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="bg-white shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between flex-wrap gap-4">
          <div class="flex items-center">
            <i class="fas fa-image text-orange-500 text-3xl mr-3"></i>
            <div>
              <h1 class="text-2xl font-bold text-gray-900">Photos base64 en base de données</h1>
              <p class="text-sm text-gray-500 mt-1">
                Liste les visites dont les photos sont stockées en base64 au lieu d'être sur disque.
              </p>
            </div>
          </div>
          <div class="flex items-center gap-3 flex-wrap">
            <button @click="loadData" :disabled="loading"
              class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
              <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'" class="mr-2"></i>
              Actualiser
            </button>
            <button @click="doDryRun" :disabled="fixing || loading || total === 0"
              class="inline-flex items-center px-4 py-2 border border-orange-300 text-sm font-medium rounded-md text-orange-700 bg-orange-50 hover:bg-orange-100 disabled:opacity-50">
              <i class="fas fa-eye mr-2"></i>
              Simuler tout corriger
            </button>
            <button @click="fixAll" :disabled="fixing || loading || total === 0"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50">
              <i :class="fixing ? 'fas fa-spinner fa-spin' : 'fas fa-wrench'" class="mr-2"></i>
              {{ fixing ? 'Correction en cours…' : `Tout corriger (${total})` }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      <!-- Dry-run result -->
      <div v-if="dryRunResult" class="mb-6 bg-blue-50 border border-blue-200 rounded-md p-4">
        <div class="flex items-start gap-3">
          <i class="fas fa-info-circle text-blue-500 mt-0.5"></i>
          <div>
            <p class="font-medium text-blue-800">Simulation — aucune modification effectuée</p>
            <p class="text-sm text-blue-700 mt-1">
              {{ dryRunResult.processed }} visite(s) à corriger ·
              {{ dryRunResult.totalFixed }} photo(s) seraient uploadées ·
              {{ dryRunResult.skipped }} déjà correctes
            </p>
          </div>
        </div>
      </div>

      <!-- Fix-all result -->
      <div v-if="fixAllResult" class="mb-6 rounded-md p-4"
        :class="fixAllResult.errorCount > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'">
        <div class="flex items-start gap-3">
          <i :class="fixAllResult.errorCount > 0 ? 'fas fa-exclamation-triangle text-yellow-500' : 'fas fa-check-circle text-green-500'" class="mt-0.5"></i>
          <div>
            <p class="font-medium" :class="fixAllResult.errorCount > 0 ? 'text-yellow-800' : 'text-green-800'">Correction terminée</p>
            <p class="text-sm mt-1" :class="fixAllResult.errorCount > 0 ? 'text-yellow-700' : 'text-green-700'">
              {{ fixAllResult.processed }} visite(s) traitée(s) · {{ fixAllResult.totalFixed }} photo(s) uploadée(s) · {{ fixAllResult.errorCount }} erreur(s)
            </p>
            <ul v-if="fixAllResult.errors && fixAllResult.errors.length" class="mt-2 text-xs text-yellow-700 list-disc list-inside">
              <li v-for="e in fixAllResult.errors" :key="e.visitId">Visite {{ e.visitId }} : {{ e.errors.join(', ') }}</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Filtre -->
      <div class="mb-4 flex items-center gap-3">
        <div class="relative">
          <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <i class="fas fa-search text-gray-400 text-xs"></i>
          </span>
          <input v-model="nsilFilter" @input="onFilterChange" type="text" placeholder="Filtrer par NSIL…"
            class="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500 w-48" />
        </div>
        <span class="text-sm text-gray-500">{{ total }} visite(s) avec photos en base64</span>
      </div>

      <div v-if="loading" class="text-center py-16">
        <i class="fas fa-spinner fa-spin text-orange-500 text-5xl mb-4"></i>
        <p class="text-gray-600">Chargement…</p>
      </div>
      <div v-else-if="!loading && rows.length === 0" class="text-center py-16">
        <i class="fas fa-check-circle text-green-400 text-6xl mb-4"></i>
        <p class="text-gray-600 text-lg">Aucune visite avec photo en base64 !</p>
      </div>

      <!-- Tableau -->
      <div v-else class="bg-white shadow rounded-lg overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NSIL</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date visite</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Technicien</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Photos</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Base64</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="row in rows" :key="row.visitId" :class="fixedVisits[row.visitId] ? 'bg-green-50' : 'hover:bg-gray-50'">
              <td class="px-4 py-3 text-sm font-mono font-medium text-gray-900">{{ row.nsil }}</td>
              <td class="px-4 py-3 text-sm text-gray-700">{{ row.address }}</td>
              <td class="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{{ formatDate(row.createdAt) }}</td>
              <td class="px-4 py-3 text-sm text-gray-700">{{ row.technician || '—' }}</td>
              <td class="px-4 py-3 text-sm text-center">{{ row.totalPhotos }}</td>
              <td class="px-4 py-3 text-center">
                <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">{{ row.base64Photos }}</span>
              </td>
              <td class="px-4 py-3 text-center">
                <div v-if="fixedVisits[row.visitId]" class="inline-flex items-center gap-1 text-green-600 text-sm font-medium">
                  <i class="fas fa-check-circle"></i> Corrigé
                </div>
                <div v-else-if="fixErrors[row.visitId]" class="text-xs text-red-600">{{ fixErrors[row.visitId] }}</div>
                <button v-else @click="fixOne(row)" :disabled="fixingVisit[row.visitId]"
                  class="inline-flex items-center px-3 py-1 border border-orange-300 text-xs font-medium rounded text-orange-700 bg-orange-50 hover:bg-orange-100 disabled:opacity-50">
                  <i :class="fixingVisit[row.visitId] ? 'fas fa-spinner fa-spin' : 'fas fa-wrench'" class="mr-1"></i>
                  {{ fixingVisit[row.visitId] ? 'En cours…' : 'Corriger' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="totalPages > 1" class="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <p class="text-sm text-gray-700">Page {{ page }} / {{ totalPages }}</p>
          <div class="flex gap-2">
            <button @click="changePage(page - 1)" :disabled="page <= 1" class="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-50">← Précédent</button>
            <button @click="changePage(page + 1)" :disabled="page >= totalPages" class="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-50">Suivant →</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import api from '../../services/api'

export default {
  name: 'AdminBase64PhotosFix',
  data() {
    return {
      loading: false, fixing: false,
      rows: [], total: 0, page: 1, limit: 50,
      nsilFilter: '', filterTimeout: null,
      fixingVisit: {}, fixedVisits: {}, fixErrors: {},
      dryRunResult: null, fixAllResult: null,
    }
  },
  computed: {
    totalPages() { return Math.ceil(this.total / this.limit) }
  },
  mounted() { this.loadData() },
  methods: {
    formatDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    onFilterChange() {
      clearTimeout(this.filterTimeout)
      this.filterTimeout = setTimeout(() => { this.page = 1; this.loadData() }, 400)
    },
    changePage(p) { this.page = p; this.loadData() },
    async loadData() {
      this.loading = true; this.dryRunResult = null; this.fixAllResult = null
      try {
        const res = await api.getBase64Photos(this.page, this.limit, this.nsilFilter)
        this.rows = res.data ?? []; this.total = res.total ?? 0
      } catch (e) { console.error('Erreur chargement', e) }
      finally { this.loading = false }
    },
    async doDryRun() {
      this.fixing = true; this.dryRunResult = null; this.fixAllResult = null
      try {
        const res = await api.fixBase64PhotosAll(true)
        this.dryRunResult = res
      } catch (e) { console.error('Erreur simulation', e) }
      finally { this.fixing = false }
    },
    async fixAll() {
      if (!confirm(`Corriger toutes les ${this.total} visite(s) avec photos en base64 ?\nCette opération est irréversible.`)) return
      this.fixing = true; this.dryRunResult = null; this.fixAllResult = null
      try {
        const res = await api.fixBase64PhotosAll(false)
        this.fixAllResult = res
        await this.loadData()
      } catch (e) { console.error('Erreur fix-all', e) }
      finally { this.fixing = false }
    },
    async fixOne(row) {
      this.fixingVisit = { ...this.fixingVisit, [row.visitId]: true }
      try {
        const res = await api.fixBase64PhotosOne(row.visitId)
        if (res.fixed > 0) {
          this.fixedVisits = { ...this.fixedVisits, [row.visitId]: true }
          this.total = Math.max(0, this.total - 1)
        } else {
          this.fixErrors = { ...this.fixErrors, [row.visitId]: 'Aucune photo corrigée' }
        }
        if (res.errors && res.errors.length) {
          this.fixErrors = { ...this.fixErrors, [row.visitId]: res.errors.join(' | ') }
        }
      } catch (e) {
        this.fixErrors = { ...this.fixErrors, [row.visitId]: e?.message || 'Erreur serveur' }
      } finally {
        this.fixingVisit = { ...this.fixingVisit, [row.visitId]: false }
      }
    }
  }
}
</script>

